/**
 * SidebarUserFooter — bottom of the sidebar, Sprint 1.5.
 *
 * Replaces the prior bottom row of (Help · Settings · Sign Out) buttons
 * with a KinGuardian-pattern user identity card + 3-state theme toggle.
 *
 *   ┌──────────────────────────────┐
 *   │ ⬤ Aalpen Patel               │
 *   │   Owner                       │
 *   ├──────────────────────────────┤
 *   │ [Auto] [Light] [Dark]         │
 *   └──────────────────────────────┘
 *
 * What moved where in Sprint 1.5:
 *   - Help → topbar lifebuoy icon + Settings drilldown "Help & support"
 *           + dropdown "Help & support" (3 access paths)
 *   - Settings → sidebar Settings drilldown group + dropdown "Settings"
 *   - Sign Out → topbar avatar dropdown last item (danger style)
 *
 * Theme toggle binds to themeMode ('auto'|'light'|'dark'). 'Auto' follows
 * the OS preference via the prefers-color-scheme listener wired in App.tsx.
 */
import { Monitor, Sun, Moon } from 'lucide-react';
import { useAppStore } from '@/stores/useAppStore';
import { useRole } from '@/hooks/useRole';

export function SidebarUserFooter() {
  const { user, themeMode, setThemeMode } = useAppStore();
  const { role } = useRole();

  // Identity card initials. Two letters max; falls back to "?" if missing.
  const initials = (user?.fullName ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('') || '?';

  const displayName = user?.fullName ?? 'Account';

  // Display role: capitalize first letter; "platform_admin" → "Platform admin".
  const displayRole = role
    ? role.split('_').map((s) => s[0].toUpperCase() + s.slice(1)).join(' ')
    : '';

  // Theme button factory — keeps the row layout obvious + accessible.
  const ThemeBtn = ({
    mode,
    icon: Icon,
    label,
  }: {
    mode: 'auto' | 'light' | 'dark';
    icon: typeof Monitor;
    label: string;
  }) => {
    const active = themeMode === mode;
    return (
      <button
        type="button"
        onClick={() => setThemeMode(mode)}
        aria-pressed={active}
        title={mode === 'auto' ? 'Auto — match system preference' : `${label} theme`}
        data-testid={`theme-btn-${mode}`}
        className={`flex items-center justify-center gap-1 px-1.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
          active
            ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100'
            : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'
        }`}
      >
        <Icon className="w-3 h-3" />
        <span>{label}</span>
      </button>
    );
  };

  return (
    <div
      className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-3 py-3 space-y-2"
      data-testid="sidebar-user-footer"
    >
      {/* Identity row */}
      <div className="flex items-center gap-2.5 px-1">
        <div
          className="w-8 h-8 rounded-full bg-primary-600 dark:bg-primary-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
          aria-hidden="true"
        >
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate"
            title={displayName}
          >
            {displayName}
          </div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">
            {displayRole}
          </div>
        </div>
      </div>

      {/* 3-state theme toggle */}
      <div
        className="grid grid-cols-3 gap-1 p-1 bg-slate-100 dark:bg-slate-900/40 rounded-lg"
        role="radiogroup"
        aria-label="Theme"
      >
        <ThemeBtn mode="auto" icon={Monitor} label="Auto" />
        <ThemeBtn mode="light" icon={Sun} label="Light" />
        <ThemeBtn mode="dark" icon={Moon} label="Dark" />
      </div>
    </div>
  );
}
